import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { OwnerContext } from "../../context/OwnerContext";
import OwnerLayout from "./OwnerLayout";
import useOwnerDashboard from "./useOwnerDashboard";
import Widget from "../commons/widgets/Widget";
import WidgetGrid from "../commons/widgets/WidgetGrid";
import { FaDog, FaCalendarAlt, FaDumbbell, FaClipboardList, FaFileAlt } from 'react-icons/fa';
import '../../styles/OwnerDashboard.css';

const OwnerDashboard = () => {
    const navigate = useNavigate();
    const { owner, loading: ownerLoading, error: ownerError } = useContext(OwnerContext);
    const { dashboardData, loading: dashboardLoading, error: dashboardError, refresh } = useOwnerDashboard();

    if (ownerLoading || dashboardLoading) {
        return (
            <OwnerLayout>
                <div className="owner-dashboard-container">
                    <div className="owner-dashboard-loading">Ładowanie...</div>
                </div>
            </OwnerLayout>
        );
    }

    if (ownerError) {
        return (
            <OwnerLayout>
                <div className="owner-dashboard-container">
                    <div className="owner-dashboard-error">{ownerError}</div>
                </div>
            </OwnerLayout>
        );
    }

    const welcomeName = `${owner?.firstName} ${owner?.lastName}`;
    const dogs = dashboardData?.dogs || [];

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
        });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleTimeString('pl-PL', {
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getProgressVariant = (percentage) => {
        if (percentage >= 75) return 'success';
        if (percentage >= 50) return 'primary';
        if (percentage >= 25) return 'warning';
        return 'danger';
    };

    return (
        <OwnerLayout>
            <div className="owner-dashboard-container">
                <div className="owner-dashboard-header">
                    <h1>Witaj, {welcomeName}!</h1>
                    <p className="owner-dashboard-subtitle">Oto podsumowanie dla Twoich pupili</p>
                </div>

                {dashboardError && (
                    <Widget type="info" title="Błąd" error={dashboardError} onRetry={refresh} />
                )}

                {dogs.length === 0 ? (
                    <div className="owner-dashboard-empty">
                        <Widget type="info" title="Brak psów">
                            <Widget.EmptyState
                                message="Nie masz jeszcze dodanych psów. Dodaj swojego pierwszego psa, aby zobaczyć statystyki."
                                icon={<FaDog size={48} />}
                                action={
                                    <Widget.Button onClick={() => navigate('/owner/my-dog')}>
                                        Dodaj psa
                                    </Widget.Button>
                                }
                            />
                        </Widget>
                    </div>
                ) : (
                    <div className="owner-dashboard-dogs">
                        {dogs.map((dog) => (
                            <div key={dog.dogId} className="owner-dashboard-dog-section">
                                <div className="owner-dashboard-dog-header">
                                    <h2>
                                        <FaDog className="owner-dashboard-dog-icon" />
                                        {dog.dogName}
                                    </h2>
                                    {dog.breed && <span className="owner-dashboard-breed">{dog.breed}</span>}
                                </div>

                                <WidgetGrid columns="2" gap="medium">
                                    {/* Soonest Training Widget */}
                                    <WidgetGrid.Item>
                                        <Widget
                                            type="schedule"
                                            title="Najbliższy trening"
                                            headerAction={
                                                <Widget.Badge variant={dog.soonestTraining ? 'primary' : 'default'}>
                                                    {dog.soonestTraining?.type === 'group' ? 'Grupowy' : 'Indywidualny'}
                                                </Widget.Badge>
                                            }
                                        >
                                            {dog.soonestTraining ? (
                                                <div className="owner-dashboard-training-info">
                                                    <Widget.ListItem
                                                        primary={formatDate(dog.soonestTraining.startTime)}
                                                        secondary={`${formatTime(dog.soonestTraining.startTime)} - ${formatTime(dog.soonestTraining.endTime)}`}
                                                        icon={<FaCalendarAlt color="#7269FF" />}
                                                        status="info"
                                                    />
                                                    {dog.soonestTraining.serviceName && (
                                                        <Widget.InfoRow
                                                            label="Usługa"
                                                            value={dog.soonestTraining.serviceName}
                                                        />
                                                    )}
                                                    {dog.soonestTraining.trainerName && (
                                                        <Widget.InfoRow
                                                            label="Trener"
                                                            value={dog.soonestTraining.trainerName}
                                                        />
                                                    )}
                                                    {dog.soonestTraining.hallName && (
                                                        <Widget.InfoRow
                                                            label="Miejsce"
                                                            value={dog.soonestTraining.hallName}
                                                        />
                                                    )}
                                                    <Widget.Button
                                                        variant="outline"
                                                        fullWidth
                                                        onClick={() => navigate('/owner/trainings')}
                                                    >
                                                        Zobacz wszystkie treningi
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message="Brak zaplanowanych treningów"
                                                    icon={<FaCalendarAlt />}
                                                    action={
                                                        <Widget.Button
                                                            variant="primary"
                                                            onClick={() => navigate('/owner/calendar')}
                                                        >
                                                            Umów trening
                                                        </Widget.Button>
                                                    }
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>

                                    {/* Exercise Stats Widget */}
                                    <WidgetGrid.Item>
                                        <Widget type="stat" title="Postęp ćwiczeń">
                                            {dog.exerciseStats && dog.exerciseStats.totalExercises > 0 ? (
                                                <div className="owner-dashboard-exercise-stats">
                                                    <Widget.Progress
                                                        value={dog.exerciseStats.completionPercentage}
                                                        max={100}
                                                        label="Ukończone ćwiczenia"
                                                        variant={getProgressVariant(dog.exerciseStats.completionPercentage)}
                                                        size="large"
                                                    />
                                                    <div className="owner-dashboard-stats-row">
                                                        <Widget.Stat
                                                            value={dog.exerciseStats.completedExercises}
                                                            label="ukończonych"
                                                            size="small"
                                                        />
                                                        <Widget.Stat
                                                            value={dog.exerciseStats.pendingExercises}
                                                            label="w trakcie"
                                                            size="small"
                                                        />
                                                        <Widget.Stat
                                                            value={dog.exerciseStats.totalExercises}
                                                            label="wszystkich"
                                                            size="small"
                                                        />
                                                    </div>
                                                    <Widget.Divider />
                                                    <Widget.Button
                                                        variant="outline"
                                                        fullWidth
                                                        icon={<FaDumbbell />}
                                                        onClick={() => navigate('/owner/my-dog')}
                                                    >
                                                        Zobacz ćwiczenia
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message="Brak przypisanych ćwiczeń"
                                                    icon={<FaDumbbell />}
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>

                                    {/* Pending Reports Widget */}
                                    <WidgetGrid.Item>
                                        <Widget
                                            type="info"
                                            title="Raporty do wypełnienia"
                                            headerAction={
                                                dog.pendingReports?.length > 0 && (
                                                    <Widget.Badge variant="warning">
                                                        {dog.pendingReports.length}
                                                    </Widget.Badge>
                                                )
                                            }
                                        >
                                            {dog.pendingReports && dog.pendingReports.length > 0 ? (
                                                <div className="owner-dashboard-pending-list">
                                                    {dog.pendingReports.slice(0, 3).map((report, index) => (
                                                        <Widget.ListItem
                                                            key={report.id || index}
                                                            primary={report.name}
                                                            secondary={report.recurrence ? `Powtarzalność: ${report.recurrence.toLowerCase()}` : 'Jednorazowy'}
                                                            icon={<FaClipboardList color="#F59E0B" />}
                                                            status="warning"
                                                            onClick={() => navigate('/owner/my-dog')}
                                                        />
                                                    ))}
                                                    {dog.pendingReports.length > 3 && (
                                                        <p className="owner-dashboard-more-items">
                                                            + {dog.pendingReports.length - 3} więcej
                                                        </p>
                                                    )}
                                                    <Widget.Button
                                                        variant="success"
                                                        fullWidth
                                                        onClick={() => navigate('/owner/my-dog')}
                                                    >
                                                        Wypełnij raporty
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message="Brak raportów do wypełnienia"
                                                    icon={<FaClipboardList />}
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>

                                    {/* Pending Surveys Widget */}
                                    <WidgetGrid.Item>
                                        <Widget
                                            type="info"
                                            title="Ankiety do wypełnienia"
                                            headerAction={
                                                dog.pendingSurveys?.length > 0 && (
                                                    <Widget.Badge variant="error">
                                                        {dog.pendingSurveys.length}
                                                    </Widget.Badge>
                                                )
                                            }
                                        >
                                            {dog.pendingSurveys && dog.pendingSurveys.length > 0 ? (
                                                <div className="owner-dashboard-pending-list">
                                                    {dog.pendingSurveys.slice(0, 3).map((survey, index) => (
                                                        <Widget.ListItem
                                                            key={survey.id || index}
                                                            primary={survey.name}
                                                            secondary={`${survey.questionCount || 0} pytań`}
                                                            icon={<FaFileAlt color="#EF4444" />}
                                                            status="error"
                                                            onClick={() => navigate('/owner/my-dog')}
                                                        />
                                                    ))}
                                                    {dog.pendingSurveys.length > 3 && (
                                                        <p className="owner-dashboard-more-items">
                                                            + {dog.pendingSurveys.length - 3} więcej
                                                        </p>
                                                    )}
                                                    <Widget.Button
                                                        variant="primary"
                                                        fullWidth
                                                        onClick={() => navigate('/owner/my-dog')}
                                                    >
                                                        Wypełnij ankiety
                                                    </Widget.Button>
                                                </div>
                                            ) : (
                                                <Widget.EmptyState
                                                    message="Brak ankiet do wypełnienia"
                                                    icon={<FaFileAlt />}
                                                />
                                            )}
                                        </Widget>
                                    </WidgetGrid.Item>
                                </WidgetGrid>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </OwnerLayout>
    );
};

export default OwnerDashboard;